<template>
  <Dialog
    data-testid="devtip-bulk-import-dialog"
    :visible="visible"
    modal
    :style="{ width: '70rem' }"
    class="mx-4"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #header>
      <div class="inline-flex items-center justify-center gap-2">
        <font-awesome-icon
          class="text-2xl text-[var(--color-orange)]"
          icon="fa-regular fa-lightbulb"
        />
        <span class="font-bold whitespace-nowrap text-xl">
          {{ $t('BulkImportDialog.bulkImportDevTips') }}
        </span>
      </div>
    </template>

    <div class="p-4">
      <!-- Chunked Upload Progress -->
      <div v-if="isChunkedUpload && uploadProgress" class="mb-6">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-blue-800 font-medium">
              {{ uploadProgress.isRefreshing ? $t('BulkImportDialog.refreshingData')
               : $t('BulkImportDialog.uploadingTips') }}
            </h4>
            <span class="text-blue-600 font-semibold">
              {{ uploadProgress.percentage }}%
            </span>
          </div>

          <div class="w-full bg-blue-200 rounded-full h-2 mb-2">
            <div
              class="bg-blue-600 h-2 rounded-full transition-all duration-300"
              :style="{ width: uploadProgress.percentage + '%' }"
            ></div>
          </div>

          <div class="flex justify-between text-sm text-blue-700">
            <span v-if="!uploadProgress.isRefreshing">
              {{ $t('BulkImportDialog.chunkProgress', {
                current: uploadProgress.currentChunk,
                total: uploadProgress.totalChunks
              }) }}
            </span>
            <span v-else class="flex items-center">
              <i class="pi pi-spin pi-spinner mr-2"></i>
              {{ $t('BulkImportDialog.refreshingData') }}
            </span>
            <span>
              {{ uploadProgress.successCount }} {{ $t('BulkImportDialog.successful') }} /
              {{ uploadProgress.totalTips }} {{ $t('BulkImportDialog.total') }}
            </span>
          </div>

          <div v-if="uploadProgress.errors.length > 0" class="mt-2 text-sm text-red-600">
            <p class="font-medium">
              {{ uploadProgress.errors.length }} {{ $t('BulkImportDialog.errors') }}
            </p>
            <ul class="mt-1 max-h-32 overflow-y-auto list-disc list-inside space-y-1">
              <li v-for="(err, idx) in uploadProgress.errors" :key="`chunk-err-${idx}`">
                {{ err }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Import Options -->
      <div class="mb-6">
        <h3 class="text-lg font-semibold mb-3">
          {{ $t('BulkImportDialog.importOptions') }}
        </h3>

        <div class="space-y-3">
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              v-model="importOptions.skipDuplicates"
              class="rounded border-gray-300"
            />
            <span>{{ $t('BulkImportDialog.skipDuplicates') }}</span>
          </label>

          <p class="text-sm text-gray-600">
            {{ $t('BulkImportDialog.skipDuplicatesDescription') }}
          </p>
        </div>
      </div>

      <!-- Import Method Tabs -->
      <div class="border rounded-lg">
        <Tabs>
          <!-- File Import Tab -->
          <TabPanel :header="$t('BulkImportDialog.fileImport')">
            <div class="space-y-4">
              <div class="mb-4">
                <h4 class="text-md font-medium mb-2">
                  {{ $t('BulkImportDialog.uploadFile') }}
                </h4>

                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg
                   p-6 text-center hover:border-gray-400 transition-colors"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    @change="handleFileSelect"
                    class="hidden"
                  />

                  <div v-if="!selectedFile" @click="triggerFileSelect" class="cursor-pointer">
                    <div class="text-4xl text-gray-400 mb-3">
                      📁
                    </div>
                    <p class="text-gray-600 mb-2">
                      {{ $t('BulkImportDialog.dragAndDrop') }}
                    </p>
                    <p class="text-sm text-gray-500">
                      {{ $t('BulkImportDialog.supportedFormats') }}
                    </p>
                    <button
                      type="button"
                      class="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      {{ $t('BulkImportDialog.selectFile') }}
                    </button>
                  </div>

                  <div v-else class="text-left">
                    <div class="flex items-center justify-between mb-3">
                      <div class="flex items-center gap-3">
                        <font-awesome-icon
                          icon="fa-file"
                          class="text-2xl text-green-500"
                        />
                        <div>
                          <p class="font-medium">{{ selectedFile.name }}</p>
                          <p class="text-sm text-gray-500">
                            {{ formatFileSize(selectedFile.size) }}
                          </p>
                        </div>
                      </div>
                      <button
                        @click="removeFile"
                        class="text-red-500 hover:text-red-700"
                        type="button"
                      >
                        <font-awesome-icon icon="fa-times" />
                      </button>
                    </div>

                    <!-- Sheet Selection (Excel files only) -->
                    <div v-if="isExcelFile && availableSheets.length > 1" class="mb-3">
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ $t('BulkImportDialog.selectSheet') }}
                      </label>
                              <Listbox
          v-model="selectedSheet"
          :options="availableSheets"
          @change="onSheetChange"
          :disabled="isProcessingSheet"
          class="w-full max-w-xs"
        />
                      <p class="text-xs text-gray-500 mt-1">
                        {{ $t('BulkImportDialog.sheetSelectionHelp') }}
                      </p>
                    </div>

                    <div
                      v-if="parsedData.length > 0"
                      class="bg-green-50 border border-green-200 rounded p-3"
                    >
                      <p class="text-green-800">
                        <font-awesome-icon icon="fa-check-circle" class="mr-2" />
                        {{ $t('BulkImportDialog.fileParsedSuccessfully',
                         { count: parsedData.length }) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Template Download -->
              <div class="p-4 bg-blue-50 border border-blue-200 rounded">
                <h4 class="text-md font-semibold mb-2 text-blue-800">
                  {{ $t('BulkImportDialog.templateDownload') }}
                </h4>
                <p class="text-blue-700 mb-3 text-sm">
                  {{ $t('BulkImportDialog.templateDescription') }}
                </p>

                <div class="flex gap-2">
                  <button
                    @click="downloadTemplate('csv')"
                    class="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    type="button"
                  >
                    {{ $t('BulkImportDialog.downloadCsvTemplate') }}
                  </button>
                  <button
                    @click="downloadTemplate('xlsx')"
                    class="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                    type="button"
                  >
                    {{ $t('BulkImportDialog.downloadExcelTemplate') }}
                  </button>
                </div>
              </div>
            </div>
          </TabPanel>

          <!-- Manual Entry Tab -->
          <TabPanel :header="$t('BulkImportDialog.manualEntry')">
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <h4 class="text-md font-medium">
                  {{ $t('BulkImportDialog.addTipsManually') }}
                </h4>
                <Button
                  :label="$t('BulkImportDialog.addTip')"
                  icon="pi pi-plus"
                  class="p-button-sm"
                  @click="addManualTip"
                />
              </div>

              <div v-if="manualTips.length === 0" class="text-center py-8 text-gray-500">
                <i class="pi pi-lightbulb text-4xl mb-2"></i>
                <p>{{ $t('BulkImportDialog.noTipsAddedYet') }}</p>
              </div>

              <div v-else class="space-y-4">
                <div
                  v-for="(tip, index) in manualTips"
                  :key="index"
                  class="border rounded-lg p-4 bg-gray-50"
                >
                  <div class="flex justify-between items-start mb-3">
                    <h5 class="font-medium">
                      {{ $t('BulkImportDialog.tip') }} {{ index + 1 }}
                    </h5>
                    <Button
                      icon="pi pi-trash"
                      class="p-button-text p-button-danger p-button-sm"
                      @click="removeManualTip(index)"
                    />
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Dimension -->
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">
                        {{ $t('BulkImportDialog.dimension') }}
                      </label>
                      <Listbox
                        v-model="tip.dimensionValue"
                        :options="dimensionOptions"
                        option-label="label"
                        option-value="value"
                        :placeholder="$t('BulkImportDialog.selectDimension')"
                        class="w-full"
                      />
                    </div>

                    <!-- Status -->
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">
                        {{ $t('BulkImportDialog.status') }}
                      </label>
                      <Listbox
                        v-model="tip.active"
                        :options="statusOptions"
                        option-label="label"
                        option-value="value"
                        :placeholder="$t('BulkImportDialog.selectStatus')"
                        class="w-full"
                      />
                    </div>

                    <!-- Title EN -->
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">
                        {{ $t('BulkImportDialog.titleEn') }} ({{ $t('BulkImportDialog.optional') }})
                      </label>
                      <InputText
                        :model-value="tip.title?.en || ''"
                        :placeholder="$t('BulkImportDialog.englishTitle')"
                        class="w-full"
                        @update:model-value="(value) => {
                          if (!tip.title) tip.title = {}; tip.title.en = value;
                        }"
                      />
                    </div>

                    <!-- Title DE -->
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">
                        {{ $t('BulkImportDialog.titleDe') }} ({{ $t('BulkImportDialog.optional') }})
                      </label>
                      <InputText
                        :model-value="tip.title?.de || ''"
                        :placeholder="$t('BulkImportDialog.germanTitle')"
                        class="w-full"
                        @update:model-value="(value) => {
                         if (!tip.title) tip.title = {}; tip.title.de = value;
                        }"
                      />
                    </div>

                    <!-- Content EN -->
                    <div class="md:col-span-2">
                      <label class="block text-xs text-gray-600 mb-1">
                        {{ $t('BulkImportDialog.contentEn') }}
                      </label>
                      <Textarea
                        v-model="tip.text.en"
                        :placeholder="$t('BulkImportDialog.englishContent')"
                        rows="3"
                        class="w-full"
                      />
                    </div>

                    <!-- Content DE -->
                    <div class="md:col-span-2">
                      <label class="block text-xs text-gray-600 mb-1">
                        {{ $t('BulkImportDialog.contentDe') }}
                      </label>
                      <Textarea
                        v-model="tip.text.de"
                        :placeholder="$t('BulkImportDialog.germanContent')"
                        rows="3"
                        class="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>

          <!-- JSON Import Tab -->
          <TabPanel :header="$t('BulkImportDialog.jsonImport')">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  {{ $t('BulkImportDialog.jsonData') }}
                </label>
                <div class="relative">
                  <Textarea
                    data-testid="devtip-import-json"
                    v-model="jsonData"
                    :placeholder="$t('BulkImportDialog.jsonPlaceholder')"
                    rows="12"
                    class="w-full font-mono text-sm border-2"
                    :class="jsonValidationStatus === 'valid' ? 'border-green-300' :
                     jsonValidationStatus === 'invalid' ? 'border-red-300' : 'border-gray-300'"
                    @input="onJsonInput"
                  />
                  <div class="absolute top-2 right-2 flex gap-1">
                    <Button
                      icon="pi pi-check"
                      class="p-button-sm p-button-text"
                      :class="jsonValidationStatus === 'valid' ? 'text-green-600' : 'text-gray-400'"
                      :title="$t('BulkImportDialog.validJson')"
                      v-if="jsonValidationStatus === 'valid'"
                    />
                    <Button
                      icon="pi pi-times"
                      class="p-button-sm p-button-text text-red-600"
                      :title="$t('BulkImportDialog.invalidJson')"
                      v-if="jsonValidationStatus === 'invalid'"
                    />
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap gap-2">
                <Button
                  :label="$t('BulkImportDialog.validateJson')"
                  icon="pi pi-check"
                  class="p-button-secondary"
                  @click="validateJson"
                />
                <Button
                  :label="$t('BulkImportDialog.formatJson')"
                  icon="pi pi-code"
                  class="p-button-outlined"
                  @click="formatJson"
                />
                <Button
                  :label="$t('BulkImportDialog.loadSample')"
                  icon="pi pi-download"
                  class="p-button-text"
                  @click="loadSampleJson"
                />
                <Button
                  :label="$t('BulkImportDialog.clearJson')"
                  icon="pi pi-trash"
                  class="p-button-text p-button-danger"
                  @click="clearJson"
                />
              </div>

              <!-- JSON Statistics -->
              <div v-if="jsonStats" class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h5 class="text-blue-800 font-medium mb-2">{{
                   $t('BulkImportDialog.jsonStatistics') }}</h5>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span class="text-blue-600 font-medium">{{
                       $t('BulkImportDialog.totalItems') }}:</span>
                    <span class="ml-1">{{ jsonStats.totalItems }}</span>
                  </div>
                  <div>
                    <span class="text-blue-600 font-medium">{{
                       $t('BulkImportDialog.validItems') }}:</span>
                    <span class="ml-1 text-green-600">{{ jsonStats.validItems }}</span>
                  </div>
                  <div>
                    <span class="text-blue-600 font-medium">{{
                       $t('BulkImportDialog.invalidItems') }}:</span>
                    <span class="ml-1 text-red-600">{{ jsonStats.invalidItems }}</span>
                  </div>
                  <div>
                    <span class="text-blue-600 font-medium">{{
                       $t('BulkImportDialog.dimensions') }}:</span>
                    <span class="ml-1">{{ jsonStats.uniqueDimensions }}</span>
                  </div>
                </div>
              </div>

              <!-- Detailed Validation Results -->
              <div v-if="jsonStats && jsonStats.invalidItems > 0"
                 class="bg-red-50 border border-red-200 rounded-lg p-3">
                <h5 class="text-red-800 font-medium mb-2">
                  <i class="pi pi-exclamation-triangle mr-1"></i>
                  {{ $t('BulkImportDialog.validationIssues', { count: jsonStats.invalidItems }) }}
                </h5>
                <div class="space-y-2 max-h-40 overflow-y-auto">
                  <div
                    v-for="(issue, index) in jsonStats.validationIssues"
                    :key="index"
                    class="text-red-700 text-sm p-2 bg-red-100 rounded border-l-4 border-red-400"
                  >
                    <div class="font-medium">{{ $t('BulkImportDialog.item') }}
                       {{ issue.index + 1 }}:</div>
                    <div class="ml-2">
                      <div v-if="issue.missingFields.length > 0">
                        <span class="font-medium">{{ $t('BulkImportDialog.missingFields') }}:</span>
                        <span class="ml-1 text-red-600">{{ issue.missingFields.join(', ') }}</span>
                      </div>
                      <div v-if="issue.invalidFields.length > 0">
                        <span class="font-medium">{{ $t('BulkImportDialog.invalidFields') }}:</span>
                        <span class="ml-1 text-red-600">{{ issue.invalidFields.join(', ') }}</span>
                      </div>
                      <div v-if="issue.title" class="text-gray-600 text-xs mt-1">
                        {{ $t('BulkImportDialog.title') }}: "{{ issue.title }}"
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </Tabs>
      </div>

      <!-- Validation Results -->
      <div v-if="validationErrors.length > 0 || fileRowErrors.length > 0 || fileProcessingError"
         class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <h4 class="text-red-800 font-medium mb-2">{{ $t('BulkImportDialog.validationErrors') }}</h4>

        <!-- File Processing Errors -->
        <div v-if="fileProcessingError" class="mb-3 p-3 bg-red-100 border border-red-300 rounded">
          <div class="flex items-start gap-2">
            <font-awesome-icon icon="fa-exclamation-triangle"
             class="text-red-600 mt-1 flex-shrink-0" />
            <div>
              <h5 class="font-medium text-red-800 mb-1">{{
                 $t('BulkImportDialog.fileProcessingError') }}</h5>
              <p class="text-red-700 text-sm">{{ fileProcessingError }}</p>
              <div class="mt-2 text-xs text-red-600">
                <p><strong>{{ $t('BulkImportDialog.expectedFormat') }}:</strong></p>
                <ul class="list-disc list-inside mt-1 space-y-1">
                  <li>{{ $t('BulkImportDialog.expectedFormatRow1') }}</li>
                  <li>{{ $t('BulkImportDialog.expectedFormatRow2') }}</li>
                  <li>{{ $t('BulkImportDialog.expectedFormatRow3') }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Skipped / invalid file rows (FA-05.19) — do not block import of valid rows -->
        <div v-if="fileRowErrors.length > 0" class="mb-3">
          <h5 class="font-medium text-red-800 mb-2">
            {{ $t('BulkImportDialog.skippedRows', { count: fileRowErrors.length }) }}
          </h5>
          <p class="text-red-700 text-sm mb-2">
            {{ $t('BulkImportDialog.skippedRowsHint') }}
          </p>
          <ul class="text-red-700 text-sm space-y-1 max-h-40 overflow-y-auto">
            <li v-for="(error, idx) in fileRowErrors" :key="`file-row-${idx}`">
              {{ error }}
            </li>
          </ul>
        </div>

        <!-- Validation Errors -->
        <div v-if="validationErrors.length > 0">
          <h5 class="font-medium text-red-800 mb-2">{{
             $t('BulkImportDialog.dataValidationErrors') }}</h5>
          <ul class="text-red-700 text-sm space-y-1">
            <li v-for="(error, idx) in validationErrors" :key="`val-${idx}`">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Preview -->
      <div v-if="previewTips.length > 0" class="bg-green-50 border
         border-green-200 rounded-lg p-4 mb-4">
        <h4 class="text-green-800 font-medium mb-2">
          {{ $t('BulkImportDialog.preview', { count: previewTips.length }) }}
        </h4>
        <div class="space-y-2 max-h-40 overflow-y-auto">
          <div
            v-for="(tip, index) in previewTips"
            :key="index"
            class="text-green-700 text-sm p-2 bg-green-100 rounded"
          >
            <strong>{{ tip.title?.en || tip.title?.de || 'No Title' }}</strong> -
            {{ tip.dimensionValue }}
            <span :class="tip.active ? 'text-green-600' : 'text-orange-600'">
              ({{ tip.active ? $t('BulkImportDialog.active') : $t('BulkImportDialog.inactive') }})
            </span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between items-center">
        <div class="text-sm text-gray-600">
          <span v-if="!isChunkedUpload">
            {{ previewTips.length }} {{ $t('BulkImportDialog.tipsReadyToImport') }}
          </span>
          <span v-else-if="uploadProgress">
            {{ $t('BulkImportDialog.uploadingInChunks', {
              total: uploadProgress.totalTips,
              chunks: uploadProgress.totalChunks
            }) }}
          </span>
          <span v-else>
            {{ previewTips.length }} {{ $t('BulkImportDialog.tipsReadyToImport') }}
          </span>
        </div>
        <div class="flex gap-2">
          <Button
            :label="$t('BulkImportDialog.cancel')"
            icon="pi pi-times"
            class="p-button-text"
            :disabled="isUploading"
            @click="$emit('update:visible', false)"
          />
          <Button
            data-testid="devtip-import-submit"
            :label="isChunkedUpload ? $t('BulkImportDialog.importInChunks')
             : $t('BulkImportDialog.importTips')"
            icon="pi pi-upload"
            :disabled="previewTips.length === 0 || isUploading"
            :loading="isUploading"
            @click="handleImport"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  Dialog,
  InputText,
  Textarea,
  Listbox,
  Button,
} from 'primevue';
import Tabs from 'primevue/tabs';
import TabPanel from 'primevue/tabpanel';
import fileParserService from '@/services/fileParserService';

export default {
  name: 'BulkImportDialog',
  components: {
    Dialog,
    Tabs,
    TabPanel,
    InputText,
    Textarea,
    Listbox,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      importOptions: {
        skipDuplicates: true,
      },
      manualTips: [],
      jsonData: '',
      validationErrors: [],
      jsonValidationStatus: 'neutral', // 'neutral', 'valid', 'invalid'
      jsonStats: null,
      statusOptions: [
        { label: this.$t('BulkImportDialog.active'), value: true },
        { label: this.$t('BulkImportDialog.inactive'), value: false },
      ],
      // File import data
      selectedFile: null,
      parsedData: [],
      fileProcessingError: null,
      fileRowErrors: [],
      availableSheets: [],
      selectedSheet: null,
      isExcelFile: false,
      isProcessingSheet: false,
      // Chunked upload data
      isChunkedUpload: false,
      isUploading: false,
      uploadProgress: null,
      CHUNK_SIZE: 100, // Tips per chunk
    };
  },
  computed: {
    ...mapGetters('dimensions', ['getAllDimensions']),

    dimensionOptions() {
      const dimensions = this.getAllDimensions;
      const options = [];

      if (dimensions && dimensions.length > 0) {
        dimensions.forEach((dimension) => {
          options.push({
            label: dimension.translation?.en || dimension.value,
            value: dimension.value,
          });
        });
      }

      return options;
    },

    previewTips() {
      const tips = [];

      // Add manual tips
      this.manualTips.forEach((tip) => {
        if (this.isValidTip(tip)) {
          tips.push(tip);
        }
      });

      // Add JSON tips
      if (this.jsonData) {
        try {
          const jsonTips = JSON.parse(this.jsonData);
          if (Array.isArray(jsonTips)) {
            jsonTips.forEach((tip) => {
              if (this.isValidTip(tip)) {
                tips.push(tip);
              }
            });
          }
        } catch (error) {
          // JSON parsing error handled in validation
        }
      }

      // Add file parsed tips
      if (this.parsedData.length > 0) {
        this.parsedData.forEach((tip) => {
          if (this.isValidTip(tip)) {
            // Ensure title object exists for template compatibility
            const processedTip = { ...tip };
            if (!processedTip.title) {
              processedTip.title = { en: '', de: '' };
            }
            tips.push(processedTip);
          }
        });
      }

      return tips;
    },

    // Determine if we should use chunked upload
    shouldUseChunkedUpload() {
      return this.previewTips.length > this.CHUNK_SIZE;
    },
  },
  async mounted() {
    // Load dimensions if not already loaded
    if (this.getAllDimensions.length === 0) {
      await this.$store.dispatch('dimensions/fetchDimensions');
    }
  },
  watch: {
    visible(newVisible) {
      if (!newVisible) {
        this.resetForm();
      }
    },
  },
  methods: {
    ...mapActions('dimensions', ['fetchDimensions']),
    ...mapActions('content', ['bulkImportDevTips']),

    resetForm() {
      this.manualTips = [];
      this.jsonData = '';
      this.validationErrors = [];
      this.jsonValidationStatus = 'neutral';
      this.jsonStats = null;
      this.importOptions.skipDuplicates = true;
      this.selectedFile = null;
      this.parsedData = [];
      this.fileProcessingError = null;
      this.fileRowErrors = [];
      this.availableSheets = [];
      this.selectedSheet = null;
      this.isExcelFile = false;
      this.isProcessingSheet = false;
      // Reset chunked upload state
      this.isChunkedUpload = false;
      this.isUploading = false;
      this.uploadProgress = null;
    },

    addManualTip() {
      this.manualTips.push({
        dimensionValue: '',
        title: { en: '', de: '' },
        text: { en: '', de: '' },
        active: true,
      });
    },

    removeManualTip(index) {
      this.manualTips.splice(index, 1);
    },

    validateTip(tip) {
      const missingFields = [];
      const invalidFields = [];

      // Check required fields
      if (!tip.dimensionValue) {
        missingFields.push('dimensionValue');
      }

      // Titles are optional - no validation needed

      if (!tip.text?.en?.trim()) {
        missingFields.push('text.en');
      }

      if (!tip.text?.de?.trim()) {
        missingFields.push('text.de');
      }

      if (tip.active === undefined || tip.active === null) {
        missingFields.push('active');
      }

      // Check field types and formats
      if (tip.active !== undefined && tip.active !== null && typeof tip.active !== 'boolean') {
        invalidFields.push('active (must be boolean)');
      }

      if (tip.dimensionValue && typeof tip.dimensionValue !== 'string') {
        invalidFields.push('dimensionValue (must be string)');
      }

      if (tip.title && typeof tip.title !== 'object') {
        invalidFields.push('title (must be object)');
      }

      if (tip.text && typeof tip.text !== 'object') {
        invalidFields.push('text (must be object)');
      }

      // Check if dimension exists in available dimensions
      // Note: We use getAllDimensions from store, not dimensionOptions (which may be filtered)
      if (tip.dimensionValue && this.getAllDimensions.length > 0) {
        const availableDimensions = this.getAllDimensions.map((dim) => dim.value);
        if (!availableDimensions.includes(tip.dimensionValue)) {
          invalidFields.push(`dimensionValue ("${tip.dimensionValue}" not found in available dimensions)`);
        }
      }

      return {
        isValid: missingFields.length === 0 && invalidFields.length === 0,
        missingFields,
        invalidFields,
      };
    },

    isValidTip(tip) {
      const validation = this.validateTip(tip);
      return validation.isValid;
    },

    onJsonInput() {
      // Auto-validate JSON as user types
      this.validateJsonOnInput();
    },

    validateJsonOnInput() {
      if (!this.jsonData.trim()) {
        this.jsonValidationStatus = 'neutral';
        this.jsonStats = null;
        return;
      }

      try {
        const data = JSON.parse(this.jsonData);
        this.jsonValidationStatus = 'valid';
        this.calculateJsonStats(data);
      } catch (error) {
        this.jsonValidationStatus = 'invalid';
        this.jsonStats = null;
      }
    },

    calculateJsonStats(data) {
      if (!Array.isArray(data)) {
        this.jsonStats = null;
        return;
      }

      const validationIssues = [];
      let validItems = 0;

      data.forEach((tip, index) => {
        const validation = this.validateTip(tip);
        if (validation.isValid) {
          validItems += 1;
        } else {
          validationIssues.push({
            index,
            missingFields: validation.missingFields,
            invalidFields: validation.invalidFields,
            title: tip.title?.en || tip.title?.de || 'Untitled',
          });
        }
      });

      const invalidItems = data.length - validItems;
      const uniqueDimensions = new Set(data.map((tip) => tip.dimensionValue).filter(Boolean)).size;

      this.jsonStats = {
        totalItems: data.length,
        validItems,
        invalidItems,
        uniqueDimensions,
        validationIssues,
      };
    },

    formatJson() {
      if (!this.jsonData.trim()) return;

      try {
        const data = JSON.parse(this.jsonData);
        this.jsonData = JSON.stringify(data, null, 2);
        this.validateJsonOnInput();
      } catch (error) {
        // If JSON is invalid, don't format it
      }
    },

    clearJson() {
      this.jsonData = '';
      this.jsonValidationStatus = 'neutral';
      this.jsonStats = null;
      this.validationErrors = [];
    },

    validateJson() {
      this.validationErrors = [];

      if (!this.jsonData.trim()) {
        this.validationErrors.push(this.$t('BulkImportDialog.jsonDataRequired'));
        return;
      }

      try {
        const data = JSON.parse(this.jsonData);

        if (!Array.isArray(data)) {
          this.validationErrors.push(this.$t('BulkImportDialog.jsonMustBeArray'));
          return;
        }

        data.forEach((tip, index) => {
          const validation = this.validateTip(tip);
          if (!validation.isValid) {
            const issues = [];
            if (validation.missingFields.length > 0) {
              issues.push(`${this.$t('BulkImportDialog.missing')}: ${validation.missingFields.join(', ')}`);
            }
            if (validation.invalidFields.length > 0) {
              issues.push(`${this.$t('BulkImportDialog.invalid')}: ${validation.invalidFields.join(', ')}`);
            }
            this.validationErrors.push(`${this.$t('BulkImportDialog.tip')} ${index + 1}: ${issues.join('; ')}`);
          }
        });
      } catch (error) {
        this.validationErrors.push(this.$t('BulkImportDialog.invalidJsonFormat'));
      }
    },

    loadSampleJson() {
      this.jsonData = JSON.stringify([
        {
          title: {
            en: 'Improve Communication Skills',
            de: 'Kommunikationsfähigkeiten verbessern',
          },
          text: {
            en: 'Practice active listening and clear expression to enhance your communication effectiveness.',
            de: 'Üben Sie aktives Zuhören und klaren Ausdruck, um Ihre Kommunikationseffektivität zu verbessern.',
          },
          dimensionValue: 'communication',
          active: true,
        },
        {
          title: {
            en: 'Develop Leadership Qualities',
            de: 'Führungsqualitäten entwickeln',
          },
          text: {
            en: 'Focus on building trust, inspiring others, and making strategic decisions.',
            de: 'Konzentrieren Sie sich darauf, Vertrauen aufzubauen, andere zu inspirieren und strategische Entscheidungen zu treffen.',
          },
          dimensionValue: 'leadership',
          active: true,
        },
      ], null, 2);
    },

    // File import methods
    triggerFileSelect() {
      this.$refs.fileInput.click();
    },

    async handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Clear any previous errors
      this.fileProcessingError = null;
      this.fileRowErrors = [];
      this.validationErrors = [];

      if (!fileParserService.isFileTypeSupported(file)) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('BulkImportDialog.unsupportedFileType'),
          detail: this.$t('BulkImportDialog.unsupportedFileTypeDetail'),
          life: 5000,
        });
        return;
      }

      this.selectedFile = file;

      // Check if it's an Excel file and get available sheets
      this.isExcelFile = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
      console.log('File is Excel:', this.isExcelFile);

      if (this.isExcelFile) {
        try {
          console.log('Getting Excel sheet names...');
          this.availableSheets = await fileParserService.getExcelSheetNames(file);
          console.log('Available sheets:', this.availableSheets);

          [this.selectedSheet] = this.availableSheets; // Default to first sheet
          console.log('Default selected sheet:', this.selectedSheet);

          if (this.availableSheets.length > 1) {
            console.log('Multiple sheets found, waiting for user selection');
            // Don't auto-parse, let user select sheet first
            return;
          }

          console.log('Single sheet, proceeding to parse');
        } catch (error) {
          console.error('Error getting sheet names:', error);
          this.fileProcessingError = error.message;
          return;
        }
      }

      // For CSV files or single-sheet Excel files, parse immediately
      await this.parseFile();
    },

    removeFile() {
      this.selectedFile = null;
      this.parsedData = [];
      this.fileProcessingError = null;
      this.fileRowErrors = [];
      this.availableSheets = [];
      this.selectedSheet = null;
      this.isExcelFile = false;
      this.isProcessingSheet = false;
      this.$refs.fileInput.value = '';
    },

    async parseFile() {
      console.log('=== PARSE FILE START ===');
      console.log('parseFile called with sheet:', this.selectedSheet);
      console.log('File type:', this.selectedFile.name.toLowerCase());
      console.log('File size:', this.selectedFile.size);

      try {
        // Clear any previous errors
        this.fileProcessingError = null;
        this.fileRowErrors = [];
        console.log('Cleared file processing error');

        let parseResult;
        if (this.selectedFile.name.toLowerCase().endsWith('.csv')) {
          console.log('Parsing CSV file...');
          const startTime = Date.now();
          parseResult = await fileParserService.parseCsvFile(this.selectedFile);
          const endTime = Date.now();
          console.log(`CSV parsing completed in ${endTime - startTime}ms`);
        } else {
          // For Excel files, use the selected sheet
          console.log('Parsing Excel file with sheet:', this.selectedSheet);
          console.log('Available sheets:', this.availableSheets);
          const startTime = Date.now();
          parseResult = await fileParserService
            .parseExcelFile(this.selectedFile, this.selectedSheet);
          const endTime = Date.now();
          console.log(`Excel parsing completed in ${endTime - startTime}ms`);
        }

        this.parsedData = parseResult.tips || [];
        this.fileRowErrors = parseResult.errors || [];

        console.log('Parsing successful, data count:', this.parsedData.length);
        console.log('Row errors:', this.fileRowErrors.length);
        console.log('First few items:', this.parsedData.slice(0, 2));

        if (this.fileRowErrors.length > 0) {
          this.$toast.add({
            severity: 'warn',
            summary: this.$t('BulkImportDialog.fileParsedWithErrors'),
            detail: this.$t('BulkImportDialog.fileParsedWithErrorsDetail', {
              valid: this.parsedData.length,
              failed: this.fileRowErrors.length,
            }),
            life: 6000,
          });
        } else {
          this.$toast.add({
            severity: 'success',
            summary: this.$t('BulkImportDialog.fileParsed'),
            detail: this.$t('BulkImportDialog.fileParsedDetail', { count: this.parsedData.length }),
            life: 3000,
          });
        }

        console.log('=== PARSE FILE SUCCESS END ===');
      } catch (error) {
        console.error('=== PARSE FILE ERROR ===');
        console.error('Error details:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);

        // Set the error message to display in the overlay
        this.fileProcessingError = error.message;
        this.fileRowErrors = [];

        this.$toast.add({
          severity: 'error',
          summary: this.$t('BulkImportDialog.parseError'),
          detail: error.message,
          life: 5000,
        });

        // Don't remove the file so user can see the error and fix it
        this.parsedData = [];

        console.log('=== PARSE FILE ERROR END ===');
      }
    },

    async onSheetChange() {
      console.log('=== SHEET CHANGE START ===');
      console.log('Sheet changed to:', this.selectedSheet);
      console.log('Current file:', this.selectedFile?.name);
      console.log('Is processing:', this.isProcessingSheet);

      // Prevent multiple rapid calls
      if (this.isProcessingSheet) {
        console.log('Already processing sheet, skipping...');
        return;
      }

      if (this.selectedSheet && this.selectedFile) {
        try {
          console.log('Setting processing flag to true');
          this.isProcessingSheet = true;

          console.log('Clearing previous data...');
          // Clear previous data
          this.parsedData = [];
          this.fileProcessingError = null;

          console.log('About to call parseFile...');
          // Parse the new sheet with timeout protection
          const parsePromise = this.parseFile();
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Parse operation timed out after 30 seconds')), 30000);
          });

          await Promise.race([parsePromise, timeoutPromise]);
          console.log('parseFile completed successfully');
        } catch (error) {
          console.error('Error parsing sheet:', error);
          this.fileProcessingError = error.message;
        } finally {
          console.log('Setting processing flag to false');
          this.isProcessingSheet = false;
          console.log('=== SHEET CHANGE END ===');
        }
      } else {
        console.log('Missing required data - selectedSheet:', this.selectedSheet, 'selectedFile:', this.selectedFile);
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
    },

    downloadTemplate(type) {
      const headers = ['title_en', 'title_de', 'text_en', 'text_de', 'dimensionValue', 'active'];
      const sampleData = [
        ['Communication Tip 1', 'Kommunikation Tipp 1', 'Improve your communication skills', 'Verbessern Sie Ihre Kommunikationsfähigkeiten', 'communication', 'true'],
        ['Leadership Tip 1', 'Führung Tipp 1', 'Develop your leadership abilities', 'Entwickeln Sie Ihre Führungsfähigkeiten', 'leadership', 'true'],
      ];

      if (type === 'csv') {
        this.downloadCsvTemplate(headers, sampleData);
      } else {
        this.downloadExcelTemplate(headers, sampleData);
      }
    },

    downloadCsvTemplate(headers, sampleData) {
      const csvContent = [headers, ...sampleData]
        .map((row) => row.map((cell) => `"${cell}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'devtips_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    },

    downloadExcelTemplate(headers, sampleData) {
      // For Excel, we'll create a simple CSV that can be opened in Excel
      this.downloadCsvTemplate(headers, sampleData);
    },

    async handleImport() {
      if (this.previewTips.length === 0) {
        return;
      }

      // Determine if we should use chunked upload
      this.isChunkedUpload = this.shouldUseChunkedUpload;

      if (this.isChunkedUpload) {
        await this.handleChunkedImport();
      } else {
        await this.handleRegularImport();
      }
    },

    async handleRegularImport() {
      try {
        // Parent runs the API import and shows success/partial-failure toasts (FA-05.19).
        this.$emit('import', {
          tips: this.previewTips,
          skipDuplicates: this.importOptions.skipDuplicates,
        });
      } catch (error) {
        console.error('Import error:', error);
        this.$toast.add({
          severity: 'error',
          summary: this.$t('BulkImportDialog.importError'),
          detail: error.message,
          life: 5000,
        });
      }
    },

    async handleChunkedImport() {
      try {
        this.isUploading = true;

        // Split tips into chunks
        const chunks = this.splitIntoChunks(this.previewTips, this.CHUNK_SIZE);
        const totalChunks = chunks.length;
        const sessionId = `upload-session-${Date.now()}`;

        // Initialize progress tracking
        this.uploadProgress = {
          currentChunk: 0,
          totalChunks,
          totalTips: this.previewTips.length,
          successCount: 0,
          failureCount: 0,
          percentage: 0,
          errors: [],
        };

        // Upload each chunk sequentially
        const uploadChunk = async (chunk, chunkNumber) => {
          try {
            // Update progress
            this.uploadProgress.currentChunk = chunkNumber;
            this.uploadProgress.percentage = Math.round((chunkNumber / totalChunks) * 100);

            // Upload chunk
            const result = await this.$store.dispatch('devTips/chunkedBulkImport', {
              tips: chunk,
              skipDuplicates: this.importOptions.skipDuplicates,
              chunkNumber,
              totalChunks,
              sessionId,
            });

            // Update progress with results
            this.uploadProgress.successCount += result.successCount;
            this.uploadProgress.failureCount += result.failureCount;
            this.uploadProgress.errors.push(...result.errors);

            return result;
          } catch (error) {
            console.error(`Chunk ${chunkNumber} upload failed:`, error);
            this.uploadProgress.failureCount += chunk.length;
            this.uploadProgress.errors.push(`Chunk ${chunkNumber}: ${error.message}`);
            throw error;
          }
        };

        // Process chunks sequentially
        for (let i = 0; i < chunks.length; i += 1) {
          const chunk = chunks[i];
          const chunkNumber = i + 1;

          // eslint-disable-next-line no-await-in-loop
          await uploadChunk(chunk, chunkNumber);

          // Small delay between chunks to avoid overwhelming the server
          if (i < chunks.length - 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => {
              setTimeout(resolve, 100);
            });
          }
        }

        // Show final results
        const successRate = Math.round(
          (this.uploadProgress.successCount / this.previewTips.length) * 100,
        );

        this.$toast.add({
          severity: successRate === 100 ? 'success' : 'warn',
          summary: this.$t('BulkImportDialog.chunkedImportComplete'),
          detail: [
            this.$t('BulkImportDialog.chunkedImportResults', {
              success: this.uploadProgress.successCount,
              total: this.previewTips.length,
              rate: successRate,
            }),
            ...(this.uploadProgress.errors || []).slice(0, 5),
            this.uploadProgress.errors.length > 5
              ? `…and ${this.uploadProgress.errors.length - 5} more`
              : null,
          ].filter(Boolean).join('\n'),
          life: this.uploadProgress.errors.length > 0 ? 8000 : 5000,
        });

        if (this.uploadProgress.errors.length > 0) {
          this.$store.commit('devTips/SET_ERROR', this.uploadProgress.errors.join('\n'));
        }

        // Close the dialog after a short delay
        setTimeout(async () => {
          // Show loading indicator for final refresh
          this.uploadProgress = {
            ...this.uploadProgress,
            currentChunk: this.uploadProgress.totalChunks + 1,
            percentage: 100,
            isRefreshing: true,
          };

          // Refresh both tips and statistics after all chunks are uploaded
          await this.$store.dispatch('devTips/loadTips');
          await this.$store.dispatch('devTips/loadStatistics');

          this.$emit('update:visible', false);
          this.resetForm();
        }, 2000);
      } catch (error) {
        console.error('Chunked import error:', error);
        this.$toast.add({
          severity: 'error',
          summary: this.$t('BulkImportDialog.chunkedImportError'),
          detail: error.message,
          life: 5000,
        });
      } finally {
        this.isUploading = false;
      }
    },

    splitIntoChunks(array, chunkSize) {
      const chunks = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
      }
      return chunks;
    },
  },
};
</script>

<style scoped>
:deep(.p-tabs-panels) {
  padding: 1rem 0;
}
</style>
